import { Button, Icon } from '@asgard/ui-library';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { WP_REST_API_Categories, WP_REST_API_Term } from 'wp-types';
import {
  Accordion,
  Backdrop,
  CategoryBlock,
  CategoryHeader,
  CategoryLink,
  ChevronBtn,
  CloseBtn,
  Drawer,
  Header,
  SubLink,
  SubList,
  Title
} from './CategoryDrawer.styled';

import { useI18n } from 'hooks/useI18n';
import { faHome } from '@fortawesome/pro-light-svg-icons';
import { APP_URLS, getAppUrl } from 'utils/app-urls';

type CategoryWithChildren = WP_REST_API_Term & {
  children: WP_REST_API_Categories;
};

interface Props {
  categories: CategoryWithChildren[];
  onSelect?: (slug: string) => void;
  onClose?: () => void;
}

export default function CategoryDrawer({
  categories = [],
  onSelect = () => null,
  onClose = () => null
}: Props) {
  const { t } = useI18n('blog');
  const { locale = 'en' } = useRouter();
  const [openId, setOpenId] = useState<string | null>(null);

  const blogRoot = `${getAppUrl(APP_URLS.blog, locale)}/category`;

  const toggle = (id: string) => setOpenId((p) => (p === id ? null : id));

  const goAndClose = (slug: string) => {
    onSelect(slug);
    onClose();
  };

  return (
    <Backdrop>
      <Drawer>
        <Header>
          <Title>{t('buttons.title')}</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        <Button
          variant="ghost"
          schema="neutral"
          label={t('buttons.home')}
          onClick={onClose}
          startIcon={faHome}
        />

        <Accordion>
          {categories.map((cat) => (
            <CategoryBlock key={cat.id}>
              <CategoryHeader>
                <Link href={`${blogRoot}/${cat.slug}`} passHref legacyBehavior>
                  <CategoryLink onClick={() => goAndClose(cat.slug)}>
                    {cat.name}
                  </CategoryLink>
                </Link>

                {cat.children.length > 0 && (
                  <ChevronBtn
                    aria-label={
                      openId === String(cat.id) ? 'Collapse' : 'Expand'
                    }
                    onClick={() => toggle(String(cat.id))}
                  >
                    <Icon
                      icon={
                        openId === String(cat.id) ? faChevronUp : faChevronDown
                      }
                    />
                  </ChevronBtn>
                )}
              </CategoryHeader>

              {openId === String(cat.id) && (
                <SubList>
                  {cat.children.map((sub) => (
                    <li key={sub.id}>
                      <Link
                        href={`${blogRoot}/${sub.slug}`}
                        passHref
                        legacyBehavior
                      >
                        <SubLink onClick={() => goAndClose(sub.slug)}>
                          {sub.name}
                        </SubLink>
                      </Link>
                    </li>
                  ))}
                </SubList>
              )}
            </CategoryBlock>
          ))}
        </Accordion>
      </Drawer>
    </Backdrop>
  );
}
